import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  type OnChanges,
  type OnDestroy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type {
  EditEduExpenseCategory,
  EduExpenseCategory,
} from '../../../shared/models/api/edu-expense-category.model';
import { BudgetType } from '../../../shared/models/api/edu-expense.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { EduExpenseCategoryService } from '../../../shared/services/api/edu-expense-category.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import { budgetTypeTranslation } from '../../edu-expense-create/edu-expense-create.helpers';
import type { EditEduExpenseCategoryFormValue } from './edu-expense-category-edit.models';

@Component({
  selector: 'opa-edu-expense-category-edit',
  imports: [
    CommonModule,
    PorscheDesignSystemModule,
    ReactiveFormsModule,
    LetDirective,
    TranslocoPipe,
  ],
  templateUrl: './edu-expense-category-edit.component.html',
  styleUrl: './edu-expense-category-edit.component.scss',
})
export class EduExpenseCategoryEditComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly eduExpenseCategoryService = inject(EduExpenseCategoryService);
  private readonly toastManager = inject(ToastManager);
  readonly formId = inject(FormIdService).getId();

  @Input({ required: true }) open = false;
  @Input({ required: true }) eduExpenseCategory: EduExpenseCategory | undefined = undefined;
  @Output() dismiss = new EventEmitter<boolean | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());
  readonly budgetTypes = Object.values(BudgetType);
  readonly budgetTypeTranslation = budgetTypeTranslation;

  ngOnChanges(changes: NgChanges<EduExpenseCategoryEditComponent>): void {
    if (changes.open && this.open) this.resetForm(this.eduExpenseCategory);
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(success?: boolean): void {
    this.dismiss.emit(success);
  }

  submit(): void {
    if (!this.form.valid || !this.eduExpenseCategory) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const category = this.formValueToCategory(formValue);
    this.subSink.sink = this.eduExpenseCategoryService
      .editEduExpenseCategory(this.eduExpenseCategory.id, category)
      .pipe(
        tap({
          next: (editedCategory) => {
            this.toastManager.addMessage({
              text: `Category '${editedCategory.description}' edited`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(true);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<EditEduExpenseCategoryFormValue>> {
    const form = this.formBuilder.group<ControlsOf<EditEduExpenseCategoryFormValue>>({
      description: this.formBuilder.control(null, [OpaValidators.required()]),
      budgetType: this.formBuilder.control(null, OpaValidators.required()),
    });

    return form;
  }

  private formValueToCategory(form: EditEduExpenseCategoryFormValue): EditEduExpenseCategory {
    const eduExpenseCategory: EditEduExpenseCategory = {
      name: this.eduExpenseCategory!.name,
      description: form.description!,
      budgetType: form.budgetType!,
    };
    return eduExpenseCategory;
  }

  private resetForm(eduExpenseCategory?: EduExpenseCategory): void {
    this.form.reset();
    if (!eduExpenseCategory) return;

    this.form.patchValue({
      description: eduExpenseCategory.description,
      budgetType: eduExpenseCategory.budgetType,
    });
  }
}
